<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div v-if="hasPendingTransfers && formattedBalanceAfterPending" class="my-3 ml-3">
      <div class="text-3xl text-high-emphasis">{{ formattedBalanceAfterPending }}</div>
      <v-tooltip location="bottom">
        <template #activator="{ props: tooltipProps }">
          <div
            class="mt-1 inline-flex w-fit items-center gap-1 text-sm text-medium-emphasis"
            v-bind="tooltipProps"
            tabindex="0"
          >
            {{ t("transfers.balanceAfter") }}
            <v-icon icon="mdi-information-outline" size="x-small" />
          </div>
        </template>
        <span>{{ t("transfers.balanceAfterHint") }}</span>
      </v-tooltip>
    </div>
    <v-data-table-virtual
      class="min-h-0 flex-1"
      :headers="headers"
      :items="transferRequests"
      :loading="loadingTransferRequests"
      hover
      fixed-header
      style="min-height: 0"
      item-key="id"
      :header-props="{ class: 'text-left font-bold' }"
    >
      <template #loading>
        <v-skeleton-loader type="table-tbody@2"></v-skeleton-loader>
      </template>

      <template #item="{ item, props: rowProps, itemRef }">
        <tr
          v-bind="rowProps"
          :ref="itemRef"
          :class="{ 'selected-transfer': selectedTransferRequestId === item.id }"
          style="cursor: pointer"
          @click="openTransferRequestDrawer(item.id)"
        >
          <td>
            {{ item.beneficiaryName }}
            <UnreadCommentsMark :count="unreadCount(transferRequestCommentTargets(item))" />
          </td>
          <td>{{ item.beneficiaryIban }}</td>
          <td>{{ item.formattedAmount }}</td>
          <td>{{ item.formattedDate }}</td>
          <td>
            <v-chip
              v-tooltip="item.translatedStatus.tooltip"
              label
              size="small"
              append-icon="mdi-information-outline"
              :color="item.color"
            >
              {{ item.translatedStatus.title }}
            </v-chip>
          </td>
          <td class="flex items-center">
            <template v-if="item.files?.length">
              <v-icon size="small">mdi-paperclip</v-icon>
              <div class="ml-1">{{ item.files?.length }}</div>
            </template>
          </td>
          <td>
            <v-btn size="small" variant="text" icon="mdi-eye"></v-btn>
          </td>
        </tr>
      </template>

      <template #no-data>
        <div class="text-center py-3">
          {{ t("transfers.noTransfers") }}
        </div>
      </template>
    </v-data-table-virtual>
  </div>
</template>

<script setup lang="ts">
import UnreadCommentsMark from "@/components/UnreadCommentsMark.vue";
import { useBalanceImpact } from "@/composables/useBalanceImpact";
import { useUnreadComments } from "@/composables/useComments";
import { useI18n } from "@/composables/useI18n";
import { useTransferRequests } from "@/composables/useTransferRequests";
import { useSelectedTransferRequestStore } from "@/store/selectedTransferRequest";
import { transferRequestCommentTargets } from "@/utils/comments";
import type { Timestamp } from "@firebase/firestore-types";
import { storeToRefs } from "pinia";
import { toRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const props = defineProps<{
  accountId: string;
}>();

const { t } = useI18n();
const accountId = toRef(props, "accountId");

const { selectedTransferRequestId } = storeToRefs(useSelectedTransferRequestStore());
const { openTransferRequestDrawer } = useSelectedTransferRequestStore();

const { transferRequests, loadingTransferRequests } = useTransferRequests(accountId);
const { hasPendingTransfers, formattedBalanceAfterPending } = useBalanceImpact(accountId);
const { unreadCount } = useUnreadComments(accountId);

// Comment emails link here to open the discussion
const route = useRoute();
const router = useRouter();
watch(
  () => route.query.transferRequestId,
  (transferRequestId) => {
    if (typeof transferRequestId !== "string") return;
    openTransferRequestDrawer(transferRequestId, { comments: true });
    void router.replace({ query: { ...route.query, transferRequestId: undefined } });
  },
  { immediate: true },
);

const headers = [
  { key: "beneficiaryName", title: t("transfers.beneficiaryName"), sortable: true },
  { key: "beneficiaryIban", title: t("transfers.beneficiaryIban"), sortable: true },
  {
    key: "amount",
    title: t("common.amount"),
    sortable: true,
    sort: (a: string, b: string) => parseFloat(a) - parseFloat(b),
  },
  {
    key: "createdAt",
    title: t("common.date"),
    sortable: true,
    sort: (a: Timestamp, b: Timestamp) => a.toMillis() - b.toMillis(),
  },
  { key: "status", title: t("common.status"), sortable: true },
  { key: "files", title: t("common.files"), sortable: true },
  { key: "actions", title: t("common.actions"), sortable: false },
];
</script>

<style scoped>
.selected-transfer {
  background-color: rgba(var(--v-theme-on-surface), 0.08) !important;
}
</style>
